import React, { useState } from 'react';
import {
  Table, TableHead, TableRow, TableCell, TableBody, Chip, Button,
  IconButton, Tooltip, CircularProgress, Alert, Box
} from '@mui/material';
import DownloadIcon from '@mui/icons-material/Download';
import InfoIcon from '@mui/icons-material/Info';
import { Link as RouterLink } from 'react-router-dom';
import { documentsApi, DOCUMENT_TYPE_LABELS } from '../api/documents';

export default function DocumentTable({ documents }) {
  const [downloading, setDownloading] = useState({});
  const [error, setError] = useState('');


  // Download document PDF
  const handleDownload = async (documentId, filename) => {
    setDownloading(prev => ({ ...prev, [documentId]: true }));
    setError('');

    try {
      const response = await documentsApi.downloadDocument(documentId);
      
      // Create blob URL and trigger download
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = filename || `document-${documentId}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Download failed:', err);
      setError('Failed to download document. Please try again.');
    } finally {
      setDownloading(prev => ({ ...prev, [documentId]: false }));
    }
  };


  if (!documents || documents.length === 0) {
    return (
      <Alert severity="info">
        No documents found for this bunkering event.
      </Alert>
    );
  }

  return (
    <Box>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}
      
      <Table>
        <TableHead>
          <TableRow>
            <TableCell>Document Type</TableCell>
            <TableCell>Filename</TableCell>
            <TableCell>Upload Date</TableCell>
            <TableCell>Uploaded By</TableCell>
            <TableCell>Download</TableCell>
            <TableCell>Details</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {documents.map((doc) => (
            <TableRow key={doc.id}>
              <TableCell>
                <Chip 
                  label={DOCUMENT_TYPE_LABELS[doc.doc_type] || doc.doc_type}
                  variant="outlined"
                  size="small"
                />
              </TableCell>
              <TableCell>{doc.filename}</TableCell>
              <TableCell>
                {doc.upload_date ? new Date(doc.upload_date).toLocaleDateString() : 'N/A'}
              </TableCell>
              <TableCell>{doc.uploaded_by?.name || 'Unknown'}</TableCell>
              <TableCell>
                <Tooltip title="Download PDF">
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => handleDownload(doc.id, doc.filename)}
                      disabled={downloading[doc.id]}
                    >
                      {downloading[doc.id] ? (
                        <CircularProgress size={16} />
                      ) : (
                        <DownloadIcon fontSize="small" />
                      )}
                    </IconButton>
                  </span>
                </Tooltip>
              </TableCell>
              <TableCell>
                <Button
                  component={RouterLink}
                  to={`/documents/${doc.id}`}
                  size="small"
                  variant="outlined"
                  startIcon={<InfoIcon />}
                  sx={{ textTransform: 'none' }}
                >
                  Details
                </Button>
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  );
}